import type { ReactNode } from 'react'
import { ChatInputForm } from 'src/shared/components/GlobalChatWidget/components/ChatInputForm'
import { Column } from 'src/shared/components/Semantic/all'
import {
  APPROVED_PROOFS_STATUS,
  APPROVED_QUOTE_STATUS,
  SHIPPING_QUOTE_STATUS,
} from 'src/shared/consts/quoteStatus'
import { ForceCustomerViewer } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { RequestBottomNav } from 'src/pages/_myAccount/Requests/Request/components/RequestBottomNav'
import { RequestChatSidebar } from 'src/pages/_myAccount/Requests/Request/components/RequestChatSidebar'
import { RequestFloatingNav } from 'src/pages/_myAccount/Requests/Request/components/RequestFloatingNav'
import { RequestPageContent } from 'src/pages/_myAccount/Requests/Request/components/RequestPageContent'
import {
  RequestProvider,
  type DemoAdvanceAction,
} from 'src/pages/_myAccount/Requests/Request/hooks/RequestContext'
import type { RequestTabId } from 'src/pages/_myAccount/Requests/Request/types'
import {
  DEMO_SHIPPING_LOCATION,
  buildDemoSnapshot,
  toRequestContextValue,
} from './demoData'

export const DemoWorkspace = ({
  tab,
  onTabChange,
  checkoutStatus,
  onCheckoutStatusChange,
  fullscreen = false,
  onExitFullscreen,
  headerActions,
}: {
  tab: RequestTabId
  onTabChange: (tab: RequestTabId) => void
  checkoutStatus: string
  onCheckoutStatusChange: (status: string) => void
  fullscreen?: boolean
  onExitFullscreen?: () => void
  headerActions?: ReactNode
}) => {
  const { locale, t } = useLocalization()
  const snapshot = buildDemoSnapshot(
    tab,
    locale === 'sv' ? 'sv' : 'en',
    checkoutStatus
  )

  const handleDemoAdvance = (action: DemoAdvanceAction) => {
    if (action === 'approve_quote') {
      onCheckoutStatusChange(APPROVED_QUOTE_STATUS)
      return
    }
    if (action === 'approve_proofs') {
      onCheckoutStatusChange(APPROVED_PROOFS_STATUS)
      return
    }
    onCheckoutStatusChange(SHIPPING_QUOTE_STATUS)
    onTabChange('delivery')
  }

  return (
    <ForceCustomerViewer>
      <RequestProvider
        value={toRequestContextValue(snapshot, {
          onDemoAdvance: handleDemoAdvance,
          demoShippingLocations: [DEMO_SHIPPING_LOCATION],
        })}
      >
        <Column className="relative h-full min-h-0 flex-1 overflow-hidden bg-[#f4f5f7]">
          {fullscreen ? (
            <RequestFloatingNav
              onBack={onExitFullscreen}
              exploreMode
              endActions={headerActions}
            />
          ) : null}
          <Column className="relative min-h-0 flex-1 overflow-hidden">
            <RequestPageContent activeTab={tab} />
            <RequestChatSidebar
              activeTab={tab}
              demo={{
                title: snapshot.quote.name ?? undefined,
                saraMessages: snapshot.saraMessages,
                messages: snapshot.teamMessages,
                isSaraLoading: false,
                composer: (
                  <ChatInputForm
                    conversationId={null}
                    onSubmit={async () => undefined}
                    disabled
                    isPending={false}
                    embedded
                    highlightSaraMentions
                    placeholderOverride={t(
                      'This is a demo',
                      'Det här är en demo'
                    )}
                  />
                ),
              }}
            />
            <RequestBottomNav activeTab={tab} onTabChange={onTabChange} />
          </Column>
        </Column>
      </RequestProvider>
    </ForceCustomerViewer>
  )
}
